import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";

const LOGO_SRC =
  "https://static.readdy.ai/image/14e48b2a3119a3da93cb07955cc0990c/70345e36bb0ad42c4823216a62f4fb9a.png";

type Step = "phone" | "otp" | "blocked";

export default function Login() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("phone");
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const [seconds, setSeconds] = useState(45);
  const [sending, setSending] = useState(false);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (step !== "otp") return;
    if (seconds <= 0) return;
    const timer = window.setInterval(() => {
      setSeconds((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [step, seconds]);

  const handleSendCode = (event: FormEvent) => {
    event.preventDefault();
    if (!/^05\d{8}$/.test(phone.trim())) {
      setPhoneError("أدخل رقم جوال سعودي صحيح بصيغة 05xxxxxxxx");
      return;
    }
    setPhoneError("");
    setSending(true);
    window.setTimeout(() => {
      setSending(false);
      if (phone.trim() === "0500000000") {
        setStep("blocked");
        return;
      }
      setOtp(["", "", "", ""]);
      setOtpError("");
      setSeconds(45);
      setStep("otp");
    }, 600);
  };

  const handleOtpChange = (index: number, raw: string) => {
    const value = raw.replace(/\D/g, "").slice(-1);
    const next = [...otp];
    next[index] = value;
    setOtp(next);
    setOtpError("");
    if (value && index < 3) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleVerify = (event: FormEvent) => {
    event.preventDefault();
    const code = otp.join("");
    if (code.length < 4) {
      setOtpError("الرجاء إدخال الرمز كاملًا");
      return;
    }
    if (code !== "1234") {
      setOtpError("الرمز غير صحيح");
      return;
    }
    navigate("/");
  };

  const maskedPhone = phone
    ? `05xx xxx x${phone.trim().slice(-2)}`
    : "05xx xxx xxx";

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background-50">
      <div className="hidden lg:flex flex-col justify-between bg-foreground-950 text-background-50 p-12">
        <div className="inline-flex items-center rounded-lg bg-white px-3 py-2 self-start">
          <img src={LOGO_SRC} alt="يور كاشير" className="h-9 w-auto" />
        </div>

        <div>
          <span className="inline-flex items-center gap-2 rounded-full bg-primary-500/20 px-4 py-1.5 text-sm font-medium text-primary-300">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-store-3-line"></i>
            </span>
            لوحة تحكم المحل التجاري
          </span>
          <h1 className="mt-6 text-4xl font-bold leading-tight">
            أدِر محلك وفروعك
            <br />
            من مكان واحد
          </h1>
          <p className="mt-4 max-w-md text-base leading-8 text-background-100/70">
            الطلبات، المنيو، الفروع، التقارير والمعاملات المالية — كل ذلك بواجهة
            عربية سريعة وواضحة.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {[
            { icon: "ri-flashlight-line", text: "متابعة الطلبات لحظة بلحظة" },
            { icon: "ri-bar-chart-box-line", text: "تقارير مبيعات دقيقة" },
            { icon: "ri-wallet-3-line", text: "تسويات مالية شفافة" },
            { icon: "ri-map-pin-line", text: "إدارة كل الفروع" },
          ].map((item) => (
            <div
              key={item.text}
              className="flex items-center gap-3 rounded-box bg-white/5 px-4 py-3"
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500/20 text-primary-300">
                <i className={item.icon}></i>
              </span>
              <span className="text-sm text-background-100/80">
                {item.text}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex justify-center">
            <div className="inline-flex items-center rounded-lg bg-white px-3 py-2 border border-background-200">
              <img src={LOGO_SRC} alt="يور كاشير" className="h-9 w-auto" />
            </div>
          </div>

          {step === "blocked" && (
            <div className="yc-pop bg-white rounded-card border border-red-200 shadow-card p-8 text-center">
              <div className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-red-100 text-red-500 text-3xl mb-4">
                <i className="ri-error-warning-line"></i>
              </div>
              <h2 className="text-xl font-bold text-foreground-950">
                الحساب غير مُفعّل
              </h2>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                حسابك قيد المراجعة أو تم تعليقه من قبل الإدارة. لا يمكنك تسجيل
                الدخول حاليًا.
              </p>
              <div className="mt-5 rounded-btn bg-background-50 px-4 py-3 text-sm text-foreground-600">
                للاستفسار تواصل مع الدعم:
                <br />
                <span className="font-semibold text-foreground-900">
                  920001234
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setStep("phone");
                  setPhone("");
                }}
                className="mt-6 w-full h-12 rounded-btn bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                المحاولة برقم آخر
              </button>
            </div>
          )}

          {step === "phone" && (
            <form
              onSubmit={handleSendCode}
              className="yc-fade-up bg-white rounded-card border border-background-200 shadow-card p-8"
            >
              <h2 className="text-2xl font-bold text-foreground-950">
                تسجيل الدخول
              </h2>
              <p className="mt-2 text-sm text-foreground-500">
                أدخل رقم الجوال المسجّل لاستلام رمز التحقق
              </p>

              <div className="mt-6">
                <label className="block text-sm font-medium text-foreground-800 mb-2">
                  رقم الجوال المسجّل{" "}
                  <span className="text-red-500">*</span>
                </label>
                <div
                  className={`flex items-center rounded-btn border bg-white px-3 focus-within:ring-2 focus-within:ring-primary-300 ${
                    phoneError ? "border-red-300" : "border-background-200"
                  }`}
                >
                  <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                    <i className="ri-smartphone-line"></i>
                  </span>
                  <input
                    type="tel"
                    name="phone"
                    dir="ltr"
                    value={phone}
                    onChange={(event) => {
                      setPhone(event.target.value.replace(/[^\d]/g, ""));
                      setPhoneError("");
                    }}
                    placeholder="05xxxxxxxx"
                    className="flex-1 h-12 bg-transparent px-3 text-left text-sm outline-none placeholder:text-foreground-300"
                  />
                </div>
                {phoneError ? (
                  <p className="mt-1.5 text-xs text-red-500">{phoneError}</p>
                ) : (
                  <p className="mt-1.5 text-xs text-foreground-400">
                    للتجربة: أدخل 0551234567 ثم الرمز 1234
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={sending}
                className="mt-6 w-full h-12 rounded-btn bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 disabled:opacity-60 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                {sending ? "جارٍ الإرسال..." : "تسجيل الدخول"}
              </button>

              <p className="mt-6 text-center text-sm text-foreground-500">
                ليس لديك حساب؟{" "}
                <Link
                  to="/register"
                  className="font-semibold text-primary-600 hover:text-primary-700"
                >
                  سجّل محلك الآن
                </Link>
              </p>
            </form>
          )}

          {step === "otp" && (
            <form
              onSubmit={handleVerify}
              className="yc-fade-up bg-white rounded-card border border-background-200 shadow-card p-8"
            >
              <h2 className="text-2xl font-bold text-foreground-950">
                أدخل رمز التحقق
              </h2>
              <p className="mt-2 text-sm text-foreground-500">
                تم إرسال رمز التحقق إلى{" "}
                <span dir="ltr" className="font-medium text-foreground-800">
                  {maskedPhone}
                </span>
              </p>

              <div className="mt-6 flex gap-3 justify-center" dir="ltr">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => {
                      inputsRef.current[index] = el;
                    }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(event) => handleOtpChange(index, event.target.value)}
                    className={`w-14 h-16 rounded-btn border-2 text-center text-2xl font-bold outline-none transition-colors duration-200 focus:border-primary-500 ${
                      otpError ? "border-red-300" : "border-background-200"
                    }`}
                  />
                ))}
              </div>

              {otpError && (
                <p className="mt-3 text-center text-xs text-red-500">
                  {otpError}
                </p>
              )}

              <div className="mt-4 text-center text-sm text-foreground-500">
                {seconds > 0 ? (
                  <>
                    إعادة الإرسال خلال{" "}
                    <span dir="ltr" className="font-semibold text-foreground-800">
                      00:{seconds.toString().padStart(2, "0")}
                    </span>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setSeconds(45)}
                    className="font-semibold text-primary-600 hover:text-primary-700 cursor-pointer"
                  >
                    إعادة إرسال الرمز
                  </button>
                )}
              </div>

              <button
                type="submit"
                className="mt-6 w-full h-12 rounded-btn bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                تأكيد الرمز
              </button>

              <button
                type="button"
                onClick={() => setStep("phone")}
                className="mt-3 w-full text-center text-sm text-foreground-500 hover:text-foreground-800 cursor-pointer"
              >
                تغيير رقم الجوال
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}